import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import {
  useUserProfiles,
  ROLE_OPTIONS,
  DISCIPLINE_OPTIONS,
  roleLabelFor,
  shortRoleLabel,
  roleColorClasses,
} from 'context/UserProfilesContext';
import { deriveNameFromEmail, initialsFor } from 'lib/currentUser';

// ---------------------------------------------------------------------------
// TeamRoster
//
// The heart of the Collaboration Hub: everyone who signs in against the
// same Supabase project appears here with their name, role, discipline,
// and email. RLS makes this list read-visible to anyone authenticated,
// so a Speech Therapist and a Behavior Specialist looking at the same
// URL both see each other.
//
// One "Edit my info" affordance lets the current user tweak their own
// display name / role / discipline without leaving the page — writes
// go through UserProfilesContext.updateMyProfile which enforces
// auth.uid() = id via RLS.
// ---------------------------------------------------------------------------

function formatDisplayName(profile) {
  if (!profile) return '';
  return (
    profile.displayName?.trim() ||
    deriveNameFromEmail(profile.email) ||
    profile.email ||
    'Teammate'
  );
}

const TeamRoster = () => {
  const { profiles, loading, me, updateMyProfile, isLive } = useUserProfiles();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Ephemeral edit form state — populated on "Edit" click, discarded
  // on cancel.
  const [form, setForm] = useState({ displayName: '', role: '', discipline: '' });

  const sorted = useMemo(() => {
    if (!Array.isArray(profiles)) return [];
    const copy = [...profiles];
    // Current user always first, then alphabetical by display name so
    // Aisha shows up before Zayd.
    copy.sort((a, b) => {
      if (me && a.id === me.id) return -1;
      if (me && b.id === me.id) return 1;
      const an = formatDisplayName(a).toLowerCase();
      const bn = formatDisplayName(b).toLowerCase();
      return an.localeCompare(bn);
    });
    return copy;
  }, [profiles, me]);

  const openEdit = () => {
    setForm({
      displayName: me?.displayName || deriveNameFromEmail(me?.email) || '',
      role: me?.role || '',
      discipline: me?.discipline || '',
    });
    setErr('');
    setEditing(true);
  };

  const closeEdit = () => {
    setEditing(false);
    setErr('');
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    setSaving(true);
    setErr('');
    try {
      await updateMyProfile({
        displayName: form.displayName.trim() || null,
        role: form.role || null,
        discipline: form.role === 'related_service' ? (form.discipline || null) : null,
      });
      setEditing(false);
    } catch (error) {
      setErr(error?.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-card rounded-xl border border-border overflow-hidden">
      <header className="p-5 border-b border-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon name="Users" size={18} className="text-primary" />
          <h2 className="text-base font-semibold text-card-foreground">
            Your team
          </h2>
          {!loading && (
            <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {sorted.length}
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Edit3"
          iconPosition="left"
          iconSize={14}
          onClick={openEdit}
          disabled={!me}
        >
          Edit my info
        </Button>
      </header>

      {editing && (
        <form
          onSubmit={handleSave}
          className="p-5 border-b border-border bg-muted/30 space-y-4"
        >
          <Input
            label="Your name"
            placeholder="e.g. Jane Rivera"
            value={form.displayName}
            onChange={(e) =>
              setForm((f) => ({ ...f, displayName: e.target.value }))
            }
            required
          />
          <Select
            label="Your role"
            placeholder="Choose your role"
            options={ROLE_OPTIONS}
            value={form.role}
            onChange={(next) =>
              setForm((f) => ({
                ...f,
                role: next,
                discipline: next === 'related_service' ? f.discipline : '',
              }))
            }
            required
          />
          {form.role === 'related_service' && (
            <Select
              label="Specialty"
              placeholder="Choose your specialty"
              options={DISCIPLINE_OPTIONS}
              value={form.discipline}
              onChange={(next) => setForm((f) => ({ ...f, discipline: next }))}
              description="Shows next to your name whenever you log data."
            />
          )}
          {err && (
            <p className="text-sm text-destructive" role="alert">
              {err}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeEdit}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={saving}
              disabled={saving}
              iconName="Check"
              iconPosition="left"
              iconSize={14}
            >
              Save
            </Button>
          </div>
        </form>
      )}

      <div className="divide-y divide-border">
        {loading ? (
          <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Loader2" size={16} className="animate-spin" />
            Loading team…
          </div>
        ) : sorted.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No teammates yet. Invite someone from the panel on the right.
          </div>
        ) : (
          sorted.map((p) => {
            const isMe = me?.id === p.id;
            const name = formatDisplayName(p);
            const short = shortRoleLabel(p);
            const rolePillClasses = roleColorClasses(p);
            const roleFull = p.discipline || roleLabelFor(p.role);
            const initials = initialsFor(name);
            return (
              <div
                key={p.id}
                className={`flex items-start gap-3 p-4 ${isMe ? 'bg-primary/5' : ''}`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${rolePillClasses}`}
                  aria-hidden="true"
                >
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-card-foreground truncate">
                      {name}
                      {isMe && (
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          (you)
                        </span>
                      )}
                    </p>
                    {short && (
                      <span
                        className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${rolePillClasses}`}
                      >
                        {short}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {roleFull || 'Role not set'}
                    {p.email ? (
                      <>
                        <span aria-hidden="true"> · </span>
                        {p.email}
                      </>
                    ) : null}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {!isLive && (
        <div className="px-5 py-3 text-xs text-muted-foreground bg-amber-50 border-t border-amber-200">
          Demo mode: this is a sample team. In live mode, everyone who signs
          in to your Supabase project appears here automatically.
        </div>
      )}
    </section>
  );
};

export default TeamRoster;
